import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { callPymbleApi } from "./usePymbleApi";

/**
 * Suggested times for an event.
 *
 * The contract is my-pymble-portal `docs/architecture/event-scheduling/README.md`,
 * written alongside this hook before either side existed.
 *
 * THE ASSISTANT IS NOT IN HERE, and used to be. That doc specced a second route,
 * `POST event/admin/availability/explain/`, which took a sentence and answered
 * with prose plus slots; this hook carried an `ask()` for it, gated off behind
 * `ASSISTANT_ENABLED = false` because the route was never built. It never will
 * be. The assistant is `components/eventManager/EventAssistantPanel` now, and it
 * reaches the availability service through the agent's `find_available_times`
 * tool — which calls the same `compute_availability` this hook's route calls,
 * with the same bounds, rather than a parallel one that could answer
 * differently. Both were removed rather than left gated, because a switch
 * nothing will ever turn on is a promise to the next reader that something is
 * coming.
 *
 * ⚠️ PII. Slots carry counts, never names — `FreeCount`, not "Ada is busy".
 * That is a property of the contract, not of this hook, and the reason is in
 * the design doc: naming who is unavailable turns scheduling into a report on
 * individual students' movements. Nothing here is logged or persisted.
 */

/** `POST event/admin/availability/` — live. */
export const AVAILABILITY_ENABLED = true;

const AVAILABILITY = "event/admin/availability/";

/** How long a slot is, when nobody has said otherwise. */
export const DEFAULT_DURATION_MINUTES = 45;

/**
 * How far ahead to look, when nobody has said otherwise.
 *
 * The API caps a window at 31 days. A fortnight is what the create flow needs —
 * far enough to find something, close enough that the answer is still true when
 * the timetable shifts.
 *
 * Lives here rather than in `EventCreateFlow` because the assistant needs the
 * same number: "the earliest day available" has to mean the same range however
 * it was asked for, or the two paths answer one question differently.
 */
export const SEARCH_WINDOW_DAYS = 14;

/** Slots per request. The API defaults to 5 and caps at 25. */
const PAGE_LIMIT = 5;
const MAX_LIMIT = 25;

export interface TimeSlot {
	Start: string;
	End: string;
	FreeCount: number;
	BusyCount: number;
	MatchPercent: number;
	/**
	 * "Everyone free" / "Mostly free" / "Good option".
	 *
	 * The server's wording, rendered as given. Deriving it from MatchPercent
	 * here would put the thresholds in two places and let this app and the
	 * parent portal disagree about what "mostly" means.
	 */
	Label: string;
}

/**
 * A time somebody has settled on, from either route.
 *
 * The two halves of a `TimeSlot` that anything downstream actually reads. A slot
 * chosen from the ranked list has the counts and the label too; one chosen from
 * an assistant card does not, because a card is not a row in that list. Keeping
 * the chosen value to what both can supply stops the create flow needing to know
 * which route it came from.
 */
export interface ChosenTime {
	Start: string;
	End: string;
}

export interface AvailabilityResult {
	InviteeCount: number;
	/**
	 * Invitees left out of the maths: carers, who have no timetable, and ids
	 * with no current person behind them.
	 *
	 * Not in the original design — the API added it, because counting carers
	 * would drag every percentage toward 100 and make the number less useful
	 * the more carers were invited. Surfaced rather than ignored: "62 checked"
	 * means something different when 20 people were skipped.
	 */
	ExcludedCount: number;
	Slots: TimeSlot[];
	Truncated: boolean;
}

export interface AvailabilityQuery {
	groupIds: string[];
	contactIds: string[];
	from: string;
	to: string;
	durationMinutes: number;
}

export function useEventScheduling() {
	const { instance } = useMsal();

	const [slots, setSlots] = useState<TimeSlot[]>([]);
	const [inviteeCount, setInviteeCount] = useState(0);
	const [excludedCount, setExcludedCount] = useState(0);
	const [truncated, setTruncated] = useState(false);
	const [searching, setSearching] = useState(false);
	const [error, setError] = useState("");
	/** Grows when "See more times" is used, up to the API's ceiling. */
	const [limit, setLimit] = useState(PAGE_LIMIT);

	/** Ranked slots for the current invitee set. */
	const findTimes = useCallback(
		async (q: AvailabilityQuery, wantLimit = PAGE_LIMIT) => {
			if (!AVAILABILITY_ENABLED) return;
			if (!q.groupIds.length && !q.contactIds.length) {
				setError("Choose who is going first.");
				return;
			}
			setSearching(true);
			setError("");
			try {
				const res: AvailabilityResult | null = await callPymbleApi(
					instance,
					"POST",
					AVAILABILITY,
					{
						data: {
							GroupIds: q.groupIds,
							ContactIds: q.contactIds,
							From: q.from,
							To: q.to,
							DurationMinutes: q.durationMinutes,
							Limit: Math.min(wantLimit, MAX_LIMIT),
						},
					}
				);
				setSlots(res?.Slots ?? []);
				setInviteeCount(res?.InviteeCount ?? 0);
				setExcludedCount(res?.ExcludedCount ?? 0);
				setTruncated(!!res?.Truncated);
				setLimit(Math.min(wantLimit, MAX_LIMIT));
			} catch (err) {
				/* The API's 422s are written for the person reading them — "every
				   invitee is a carer", "too many invitees" — and say nothing about
				   any individual, so they are worth showing. Anything else gets a
				   generic line: an error body can echo the request back, and the
				   request is a list of people. */
				const status = (err as { response?: { status?: number } })?.response
					?.status;
				const detail = (
					err as { response?: { data?: { detail?: unknown } } }
				)?.response?.data?.detail;
				if (status === 422 && typeof detail === "string") {
					setError(detail);
				} else if (status === 429) {
					setError("Too many requests just now. Try again in a minute.");
				} else {
					setError("Could not check availability. Please try again.");
				}
				setSlots([]);
			} finally {
				setSearching(false);
			}
		},
		[instance]
	);

	const reset = useCallback(() => {
		setSlots([]);
		setInviteeCount(0);
		setExcludedCount(0);
		setTruncated(false);
		setError("");
		setLimit(PAGE_LIMIT);
	}, []);

	return {
		enabled: AVAILABILITY_ENABLED,
		slots,
		inviteeCount,
		excludedCount,
		truncated,
		/** True while more slots can still be asked for. */
		canLoadMore: truncated && limit < MAX_LIMIT,
		limit,
		pageLimit: PAGE_LIMIT,
		searching,
		error,
		findTimes,
		reset,
	};
}
